// features/notifications/components/PushNotificationManager.tsx
//
// Mounted once near the app root (sibling of the route tree). Responsibilities:
//   1. Foreground delivery — surface incoming push messages as in-app toasts
//      while the tab is focused (background is handled by the service worker).
//   2. Token upkeep — refresh this device's FCM token on load for users who
//      already opted in (FCM rotates tokens).
//   3. Soft pre-prompt — a gentle, dismissible banner inviting the user to
//      enable notifications. We never fire the native permission prompt on
//      load; it only fires when the user opts in here. Dismissal is sticky.

import { JSX, useEffect, useState } from 'react';
import { Snackbar, Alert, Button, Box } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { onForegroundMessage, refreshToken } from '../../../services/pushNotifications';
import { usePushNotifications } from '../hooks/usePushNotifications';

const DISMISS_KEY = 'roundmaster_push_prompt_dismissed';

export function PushNotificationManager(): JSX.Element | null {
  const { user } = useAuth();
  const notify = useNotification();
  const { supported, permission, enabled, loading, busy, enable } = usePushNotifications();

  const uid = user?.uid;
  const [showPrompt, setShowPrompt] = useState(false);

  // 1. Foreground message listener. Gated behind `enabled` so the
  //    firebase/messaging SDK only dynamic-imports for users who actually
  //    opted in — users who never enable push never download it.
  useEffect(() => {
    if (!supported || !uid || !enabled || permission !== 'granted') return;
    let unsub: (() => void) | undefined;
    let cancelled = false;
    onForegroundMessage((data) => {
      notify.showNotification({ title: data.title, message: data.body, severity: 'info', duration: 8000 });
    }).then((fn) => {
      if (cancelled) fn();
      else unsub = fn;
    });
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [supported, uid, enabled, permission, notify]);

  // 2. Keep the token fresh for already-opted-in users.
  useEffect(() => {
    if (uid && enabled && permission === 'granted') {
      refreshToken(uid);
    }
  }, [uid, enabled, permission]);

  // 3. Decide whether to show the soft pre-prompt.
  useEffect(() => {
    if (loading || !supported || !uid) {
      setShowPrompt(false);
      return;
    }
    const dismissed = typeof localStorage !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1';
    setShowPrompt(permission === 'default' && !enabled && !dismissed);
  }, [loading, supported, uid, permission, enabled]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setShowPrompt(false);
  };

  const handleEnable = async () => {
    await enable();
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <Snackbar
      open={showPrompt}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ maxWidth: 460 }}
    >
      <Alert
        icon={<NotificationsActiveIcon />}
        severity="info"
        variant="filled"
        sx={{
          width: '100%',
          backgroundColor: 'rgba(0, 255, 212, 0.95)',
          color: '#0A0E14',
          '& .MuiAlert-icon': { color: '#0A0E14' },
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }}
      >
        <Box sx={{ mb: 1, fontWeight: 600 }}>Get notified on the floor</Box>
        <Box sx={{ fontSize: '0.85rem', mb: 1.5 }}>
          Turn on notifications to hear about appeals, missing tables and new round
          pairings — even when this tab is in the background.
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={dismiss} sx={{ color: '#0A0E14' }}>
            Not now
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={handleEnable}
            sx={{ backgroundColor: '#0A0E14', color: '#00FFD4', '&:hover': { backgroundColor: '#1E2530' } }}
          >
            Enable
          </Button>
        </Box>
      </Alert>
    </Snackbar>
  );
}

export default PushNotificationManager;
